import type { ProgressInfo } from '@huggingface/transformers';

/** Aggregate state of a model download, shared by both engines. */
export type ModelDownload = {
  /** 0..1 across the whole download, and monotonic. */
  progress: number;
  loadedBytes: number;
  /** Best estimate; only exact once every file has announced its size. */
  totalBytes: number;
  /** False until some file has reported a size — render an indeterminate bar. */
  determinate: boolean;
};

/**
 * transformers.js reports progress per *file*, and a model is a dozen of them
 * (config, tokenizer, encoder, decoder…). Rendering those events directly is
 * what makes the percentage bounce: the tokenizer climbs to 100%, the encoder
 * starts, and the number snaps back to 2%. The library's own aggregate
 * (`progress_total`) does not fix it either — it divides by the sum of the
 * totals *known so far*, so the denominator jumps every time a new file
 * announces its size, and the ratio drops.
 *
 * This tracker sums bytes across files, divides by a denominator seeded with
 * the model's advertised size so discovering a file cannot inflate it, and
 * never reports a value below the previous one.
 */
export const createDownloadTracker = (expectedBytes: number) => {
  const files = new Map<string, { loaded: number; total: number }>();
  let progress = 0;

  const state = (): ModelDownload => {
    let loadedBytes = 0;
    let knownTotal = 0;
    for (const f of files.values()) {
      loadedBytes += f.loaded;
      knownTotal += f.total;
    }

    const totalBytes = Math.max(knownTotal, expectedBytes, loadedBytes);
    // Deliberately capped below 1: the stage ends when the pipeline resolves —
    // weights compiled and resident on the GPU — which is a beat after the last
    // byte lands. A bar parked at 100% during that beat reads as a hang.
    const ratio = totalBytes > 0 ? Math.min(loadedBytes / totalBytes, 0.99) : 0;
    progress = Math.max(progress, ratio);

    return { progress, loadedBytes, totalBytes, determinate: knownTotal > 0 };
  };

  return {
    /** Folds one transformers.js event in; returns the aggregate to render. */
    push(info: ProgressInfo): ModelDownload {
      if (info.status === 'progress' && info.total > 0) {
        files.set(`${info.name}/${info.file}`, {
          loaded: Math.min(info.loaded, info.total),
          total: info.total,
        });
      } else if (info.status === 'done') {
        // A file can finish without a final 'progress' event — pin it, or the
        // bar stalls a few hundred kB short of where it should be.
        const f = files.get(`${info.name}/${info.file}`);
        if (f) f.loaded = f.total;
      }
      return state();
    },
  };
};

